import { InfoMessage, InfoMessageProps } from '../../components/InfoMessage';
import {
    AlertColor,
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { GridColDef, GridFooterContainer, GridPaginationModel, GridSortModel, GridToolbar } from '@mui/x-data-grid';
import FormSection from '../../components/FormSection';
import { UploadFile } from '@mui/icons-material';
import Block from '../../components/Block';
import Row from '../../components/Row';
import Column from '../../components/Column';
import CDGrid, { CustomPagination } from '../../components/CDGrid';
import CDDatePicker from '../../components/CDDatePicker';
import DeleteIcon from '@mui/icons-material/Delete';
import ApiService from '../../ApiService';
import { SelectChangeEvent } from '@mui/material/Select';
import React, { useCallback, useEffect, useState } from 'react';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { AxiosPageableResponse } from '../../types/AxiosPageableResponse';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';

import Grid from '@mui/material/Grid2';
import { ShellCampaign } from '../../types/DspEntity';
import { SectionHeading } from '../../components/SectionHeading';
import Utils from '../../components/Utils';
import { transformSortModelForBackend } from '../agency/partials/SortModelTransformer';

export default function CampaignBulkCreatePage() {
    function ShellCampaignsGridFooter(props: { totalElements: number; numberOfElements: number }) {
        return (
            <GridFooterContainer>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '10px' }}>
                    <CustomPagination />
                    <span>
                        Showing {props.numberOfElements} of {props.totalElements} records
                    </span>
                </div>
            </GridFooterContainer>
        );
    }

    const [infoMessage, setInfoMessage] = useState<InfoMessageProps>({
        message: null,
    });

    // State management
    const defaultPageSize: number = 50;
    const pageValueArray = [10, 20, 50, 75, 100];
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [numberOfElements, setNumberOfElements] = useState(0);
    const [list, setList] = useState<ShellCampaign[]>([]);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'id', sort: 'desc' }]);
    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

    // Bulk edit state
    const [editIds, setEditIds] = useState<number[]>([]);
    const [budget, setBudget] = useState<number | null>(null);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [open, setOpen] = useState(false);

    function showInfoMessage(alertColor: AlertColor, message: string) {
        setInfoMessage({
            message: message,
            severity: alertColor,
            onClose: () => {
                setInfoMessage({ message: null });
            },
        });
    }

    const handleDelete = async (campaignId: number) => {
        try {
            await ApiService.deleteCampaign(campaignId);
            await fetchData();
            showInfoMessage('success', 'Campaign deleted successfully.');
        } catch (error: any) {
            showInfoMessage('error', 'Failed to delete campaign.');
        }
    };

    const fetchData = async () => {
        const transformedSortModel = transformSortModelForBackend(sortModel);
        const response: AxiosPageableResponse<ShellCampaign> = await ApiService.getBulkCreateCampaigns(
            page,
            pageSize,
            transformedSortModel
        );
        if (response.status === 200) {
            setList(response.data.content || []);
            setTotalElements(response.data.totalElements || 0);
            setNumberOfElements(response.data.numberOfElements || 0);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.csv')) {
            showInfoMessage('error', 'Only .csv files are allowed');
            event.target.value = '';
            return;
        }

        setUploadedFileName(file?.name);

        if (file) {
            const formData = new FormData();
            formData.append('bulkShellCampaign', file);

            ApiService.uploadShellCampaignFile(formData, undefined, 'create')
                .then((response) => {
                    if (response.status === 200) {
                        fetchData();
                        showInfoMessage('success', JSON.stringify(response.data));
                    }
                })
                .catch((error) => {
                    setUploadedFileName(null);
                    event.target.value = '';
                    if (error.response.status === 422) {
                        fetchData();
                        showInfoMessage('warning', JSON.stringify(error.response?.data));
                    } else {
                        showInfoMessage('error', JSON.stringify(error.response?.data));
                    }
                });
        }
    };

    const onPageChange = useCallback((model: GridPaginationModel) => {
        setPage(model.page);
        setPageSize(model.pageSize);
    }, []);

    const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
    }, []);

    const handleShellCampaignChange = (event: SelectChangeEvent<string>) => {
        const value = parseInt(event?.target?.value);
        if (pageValueArray.includes(value)) {
            setPageSize(value);
            setPage(0);
        } else {
            setPageSize(defaultPageSize);
            setPage(0);
        }
    };

    const handleOpen = () => {
        resetForm();
        setOpen(true);
    };

    const handleClose = () => {
        resetForm();
        setOpen(false);
    };

    const resetForm = () => {
        setBudget(null);
        setStartDate(null);
        setEndDate(null);
    };

    const bulkChangeSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const payload: any = {};
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Build the payload
        if (budget !== null && budget !== undefined && !isNaN(budget)) payload.budget = budget;
        if (startDate) payload.startDate = startDate.toLocaleDateString('en-US');
        if (endDate) payload.endDate = endDate.toLocaleDateString('en-US');

        // Filter out records with past start dates if we're updating startDate
        let validIds = editIds;
        if (startDate) {
            const selectedRecords = list.filter((record) => editIds.includes(record.id));
            const recordsWithFutureStartDates = selectedRecords.filter((record) => {
                if (!record.startDate) return true;
                const existingStartDate = new Date(record.startDate);
                existingStartDate.setHours(0, 0, 0, 0);
                return existingStartDate >= today;
            });

            validIds = recordsWithFutureStartDates.map((record) => record.id);

            const skippedCount = editIds.length - validIds.length;
            if (skippedCount > 0) {
                showInfoMessage(
                    'warning',
                    `${skippedCount} record(s) were skipped because their start date is in the past.`
                );
            }
        }

        if (validIds.length === 0) {
            showInfoMessage('error', 'No records to update. All selected records have start dates in the past.');
            return;
        }

        try {
            await ApiService.updateBulkCampaigns({
                ids: validIds,
                shellCampaignDto: payload,
            });
            await fetchData();
            showInfoMessage('success', `${validIds.length} campaign(s) updated successfully.`);
        } catch (error) {
            showInfoMessage('error', 'Failed to update campaigns.');
        }

        setEditIds([]);
        handleClose();
    };

    const editModalStyles = {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        borderRadius: '8px',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        transform: 'translate(-50%, -50%)',
        width: 600,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
    };

    const columns: GridColDef[] = [
        {
            flex: 0.5,
            minWidth: 60,
            field: 'id',
            headerName: 'ID',
            type: 'number',
            headerAlign: 'center',
            align: 'center',
        },
        {
            flex: 1.5,
            minWidth: 120,
            field: 'advertiser',
            headerName: 'Advertiser',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.5,
            minWidth: 140,
            field: 'dspAdvertiserID',
            headerName: 'DSP Advertiser ID',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        fontFamily: 'monospace',
                        fontSize: '0.85em',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 0.8,
            minWidth: 80,
            field: 'platform',
            headerName: 'Platform',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1,
            minWidth: 100,
            field: 'oemProgram',
            headerName: 'OEM Program',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.5,
            minWidth: 140,
            field: 'dspCampaignID',
            headerName: 'DSP Campaign ID',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        fontFamily: 'monospace',
                        fontSize: '0.85em',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 2,
            minWidth: 150,
            field: 'campaignName',
            headerName: 'Campaign Name',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        fontWeight: '500',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1,
            minWidth: 100,
            field: 'startDate',
            headerName: 'Start Date',
            type: 'string',
            valueGetter: (params: Date | null | undefined) => Utils.getDate(params),
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        fontSize: '0.85em',
                        width: '100%',
                    }}
                >
                    {Utils.formatESTDateLong(params.value)}
                </div>
            ),
        },
        {
            flex: 1,
            minWidth: 100,
            field: 'endDate',
            headerName: 'End Date',
            type: 'string',
            valueGetter: (params: Date | null | undefined) => Utils.getDate(params),
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        fontSize: '0.85em',
                        width: '100%',
                    }}
                >
                    {Utils.formatESTDateLong(params.value)}
                </div>
            ),
        },
        {
            flex: 1,
            minWidth: 90,
            field: 'budget',
            headerName: 'Budget',
            type: 'number',
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        fontFamily: 'monospace',
                        fontWeight: '500',
                        width: '100%',
                        textAlign: 'right',
                    }}
                >
                    {Utils.formatCurrency(params.value, 0)}
                </div>
            ),
        },
        {
            flex: 1,
            minWidth: 90,
            field: 'dspBudget',
            headerName: 'DSP Budget',
            type: 'number',
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        fontFamily: 'monospace',
                        fontSize: '0.85em',
                        width: '100%',
                        textAlign: 'right',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.2,
            minWidth: 100,
            field: 'deliveryStatusId',
            headerName: 'Delivery Status',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.2,
            minWidth: 100,
            field: 'activationStatusId',
            headerName: 'Activation Status',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.2,
            minWidth: 100,
            field: 'poNumber',
            headerName: 'PO Number',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        fontFamily: 'monospace',
                        fontSize: '0.85em',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1,
            minWidth: 90,
            field: 'mediaType',
            headerName: 'Media Type',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.2,
            minWidth: 100,
            field: 'campaignType',
            headerName: 'Campaign Type',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.2,
            minWidth: 100,
            field: 'strategyType',
            headerName: 'Strategy Type',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.2,
            minWidth: 100,
            field: 'audienceType',
            headerName: 'Audience Type',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.1,
            minWidth: 95,
            field: 'bidStrategy',
            headerName: 'Bid Strategy',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 0.8,
            minWidth: 70,
            field: 'goal',
            headerName: 'Goal',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.3,
            minWidth: 110,
            field: 'budgetCapType',
            headerName: 'Budget Cap Type',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 0.6,
            minWidth: 60,
            field: 'kpi',
            headerName: 'KPI',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.2,
            minWidth: 100,
            field: 'frequencyType',
            headerName: 'Frequency Type',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.3,
            minWidth: 110,
            field: 'frequencyTimeUnitCount',
            headerName: 'Freq. Time Unit Count',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        fontSize: '0.85em',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.2,
            minWidth: 100,
            field: 'frequencyTimeUnit',
            headerName: 'Freq. Time Unit',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.3,
            minWidth: 110,
            field: 'frequencyMaxImpressions',
            headerName: 'Freq. Max Impressions',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        fontSize: '0.85em',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.2,
            minWidth: 100,
            field: 'budgetCapAmount',
            headerName: 'Budget Cap Amount',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        fontSize: '0.85em',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.4,
            minWidth: 120,
            field: 'budgetCapRecurrenceTimePeriod',
            headerName: 'Budget Cap Recurrence',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        fontSize: '0.85em',
                        width: '100%',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.3,
            minWidth: 110,
            field: 'dspCampaignSyncedDate',
            headerName: 'Last DSP Sync',
            type: 'string',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.3',
                        padding: '4px',
                        fontFamily: 'monospace',
                        fontSize: '0.8em',
                        width: '100%',
                    }}
                >
                    {params.value ? Utils.toLocalTime(params.value).toLocaleString() : null}
                </div>
            ),
        },
        {
            flex: 0.6,
            minWidth: 90,
            field: 'actions',
            headerName: 'Actions',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
                if (params.row.dspCampaignSyncedDate) {
                    return null;
                }

                return (
                    <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(params.row.id);
                        }}
                        title="Delete Campaign"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                );
            },
        },
    ];

    return (
        <>
            <InfoMessage {...infoMessage} />

            <Box sx={{ mb: 3 }}>
                <Row justifyContent="space-between" alignItems="center" spacing={2} height={64} sx={{ px: 3, py: 2 }}>
                    <SectionHeading divider={false}>
                        <Row spacing={1} alignItems="center">
                            <Typography variant="h6">Campaign Bulk Create</Typography>
                        </Row>
                    </SectionHeading>
                </Row>
            </Box>

            <Block>
                <Grid size={{ md: 6 }}>
                    <FormSection>
                        <Typography variant="h6" gutterBottom>
                            Bulk Shell Campaign Upload
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<UploadFile />}
                                size="small"
                                sx={{ minWidth: '120px', padding: '4px 8px', fontSize: '0.75rem' }}
                            >
                                Upload File
                                <input type="file" accept=".csv,text/csv" hidden onChange={handleFileUpload} />
                            </Button>
                            {uploadedFileName && (
                                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                    {uploadedFileName}
                                </Typography>
                            )}
                        </Stack>
                    </FormSection>
                </Grid>

                <Grid size={{ md: 12 }}>
                    <Column gap={2}>
                        <Row justifyContent="space-between" alignItems="center">
                            <Row gap={0} sx={{ width: '100%' }}>
                                <Typography component="span" color="text.primary" variant="subtitle2" fontWeight={500}>
                                    Shell Bulk Upload Results
                                </Typography>
                            </Row>
                            <Row flex={0} justifyContent="flex-end" spacing={1}>
                                <FormControl sx={{ minWidth: 120 }} size="small">
                                    <InputLabel id="demo-select-small-label">Rows Per Page</InputLabel>
                                    <Select
                                        labelId="demo-select-small-label"
                                        id="demo-select-small"
                                        value={pageSize.toString()}
                                        label="Rows Per Page"
                                        onChange={handleShellCampaignChange}
                                    >
                                        {pageValueArray?.map((value) => {
                                            return (
                                                <MenuItem key={value} value={value}>
                                                    {value}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                                {editIds?.length >= 1 ? (
                                    <>
                                        <Button
                                            variant="text"
                                            onClick={handleOpen}
                                            startIcon={<BorderColorIcon />}
                                            sx={{ marginTop: '-1%' }}
                                        >
                                            Update Campaigns
                                        </Button>
                                    </>
                                ) : null}
                            </Row>
                        </Row>

                        <Box
                            sx={{
                                width: '100%',
                                height: 600,
                                overflow: 'hidden',
                                '& .MuiDataGrid-root': {
                                    border: 'none',
                                    width: '100%',
                                },
                                '& .MuiDataGrid-main': {
                                    width: '100%',
                                    '& .MuiDataGrid-virtualScroller': {
                                        width: '100%',
                                        '&::-webkit-scrollbar': {
                                            width: '8px',
                                            height: '8px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            backgroundColor: '#f1f1f1',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: '#c1c1c1',
                                            borderRadius: '4px',
                                        },
                                        '&::-webkit-scrollbar-thumb:hover': {
                                            backgroundColor: '#a8a8a8',
                                        },
                                    },
                                },
                            }}
                        >
                            <CDGrid
                                id="campaignBulkCreateId"
                                variant="contained"
                                checkboxSelection
                                disableRowSelectionOnClick
                                autoHeight={false}
                                sx={{
                                    height: 600,
                                    width: '100%',
                                    '& .MuiDataGrid-main': {
                                        overflow: 'auto',
                                        width: '100%',
                                    },
                                    '& .MuiDataGrid-virtualScroller': {
                                        overflow: 'auto',
                                        width: '100%',
                                    },
                                    '& .MuiDataGrid-footerContainer': {
                                        minHeight: 52,
                                    },
                                    // Stronger selectors with !important
                                    '& .MuiDataGrid-columnHeaders': {
                                        minHeight: '100px !important',
                                        maxHeight: '100px !important',
                                        height: '100px !important',
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        whiteSpace: 'pre-line !important',
                                        lineHeight: '1.2 !important',
                                        padding: '16px 4px !important',
                                        height: '100px !important',
                                        minHeight: '100px !important',
                                        maxHeight: '100px !important',
                                        display: 'flex !important',
                                        alignItems: 'center !important',
                                    },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        whiteSpace: 'pre-line !important',
                                        textAlign: 'center !important',
                                        width: '100% !important',
                                        fontSize: '0.875rem !important',
                                        lineHeight: '1.3 !important',
                                    },
                                    // Additional selectors to ensure it works
                                    '& .MuiDataGrid-root .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
                                        height: '100px !important',
                                        minHeight: '100px !important',
                                    },
                                }}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: pageSize,
                                            page: page,
                                        },
                                    },
                                }}
                                paginationModel={{
                                    page: page,
                                    pageSize: pageSize,
                                }}
                                rowCount={totalElements}
                                rowSelectionModel={editIds}
                                selectionsChanged={(ids) => {
                                    const selectedIDs = new Set(ids);
                                    setEditIds(Array.from(selectedIDs.values()) as number[]);
                                }}
                                columns={columns}
                                onPaginationModelChange={onPageChange}
                                paginationMode="server"
                                sortingMode="server"
                                onSortModelChange={handleSortModelChange}
                                sortModel={sortModel}
                                rows={list}
                                slots={{
                                    toolbar: GridToolbar,
                                    footer: () => (
                                        <ShellCampaignsGridFooter
                                            totalElements={totalElements}
                                            numberOfElements={numberOfElements}
                                        />
                                    ),
                                }}
                                disableColumnFilter
                                disableColumnSelector
                                disableDensitySelector
                                getRowId={(row) => row.id}
                            />
                        </Box>
                    </Column>
                </Grid>

                <Modal
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <Box sx={editModalStyles}>
                        <Box sx={{ width: '100%' }}>
                            <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ textAlign: 'center' }}>
                                Bulk Edit
                            </Typography>
                            <form onSubmit={bulkChangeSubmit}>
                                <Box sx={{ mt: 2 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 2,
                                        }}
                                    >
                                        <Typography>Start Date</Typography>
                                        <CDDatePicker
                                            disablePast
                                            value={startDate}
                                            onChange={(newValue) => setStartDate(newValue)}
                                            label="Start Date"
                                            showError={false}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 2,
                                        }}
                                    >
                                        <Typography>End Date</Typography>
                                        <CDDatePicker
                                            disablePast
                                            value={endDate}
                                            onChange={(newValue) => setEndDate(newValue)}
                                            label="End Date"
                                            showError={false}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 2,
                                        }}
                                    >
                                        <Typography>Budget</Typography>
                                        <TextField
                                            type="number"
                                            value={budget || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setBudget(value === '' ? null : parseFloat(value));
                                            }}
                                            label="Budget"
                                            variant="outlined"
                                            size="small"
                                            inputProps={{
                                                min: 0,
                                                step: 0.1,
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-evenly',
                                        marginTop: '10%',
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        sx={{ width: '120px' }}
                                        startIcon={<SaveIcon />}
                                        type="submit"
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        sx={{ width: '120px' }}
                                        onClick={handleClose}
                                        startIcon={<CloseIcon />}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </form>
                        </Box>
                    </Box>
                </Modal>
            </Block>
        </>
    );
}
